import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useKeyDown } from "@react-hooks-library/core";
import { twJoin, twMerge } from "tailwind-merge";

import { IconButton } from "src/design-system/Icon";

// ============================================================================
// Drawer Types - Discriminated Union
// ============================================================================

/** Data for the flow tool configuration drawer */
export type FlowToolConfigDrawerData = {
  toolId: string;
  onRequestChangeFlow: () => void;
};

/** Data for the add tool drawer */
export type AddToolDrawerData = {
  onAddFlowTool: () => void;
};

/** Data for the JSON schema editor drawer */
export type JsonSchemaDrawerData = {
  schemaType: "input" | "output";
  readOnly?: boolean;
};

/**
 * Discriminated union for open drawer state.
 * Each drawer type has its own variant with typed data.
 * Use type narrowing: `if (openDrawer?.type === "flowToolConfig") { openDrawer.data.tool... }`
 */
export type OpenDrawerState =
  | { type: "flowToolConfig"; data: FlowToolConfigDrawerData }
  | { type: "addTool"; data: AddToolDrawerData }
  | { type: "playground" }
  | { type: "jsonSchema"; data: JsonSchemaDrawerData };

/** All possible drawer type values */
export type DrawerType = OpenDrawerState["type"];

// ============================================================================
// Coordinator Context + Provider
// ============================================================================

type SideDrawerCoordinatorContextProps = {
  /** Currently open drawer state (null if closed) */
  openDrawer: OpenDrawerState | null;
  /** Request to open a drawer - type-safe with discriminated union */
  requestOpen: (state: OpenDrawerState) => void;
  /** Request to close current drawer */
  requestClose: () => void;
  /** Specific drawer calls this when close succeeds */
  notifyCloseAccepted: () => void;
  /** Specific drawer calls this when user rejects close (e.g. "Keep editing") */
  notifyCloseRejected: () => void;
  /** Register a close handler for a drawer (internal use by useSideDrawer) */
  registerHandler: (drawerType: DrawerType, handler: () => void) => () => void;
};

const SideDrawerCoordinatorContext =
  createContext<SideDrawerCoordinatorContextProps | null>(null);

/**
 * Central coordinator for the side drawer system.
 * Manages open/close requests and transitions between drawers.
 */
export const SideDrawerCoordinatorProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Single state for open drawer - discriminated union ensures type and data stay in sync
  const [openDrawer, setOpenDrawer] = useState<OpenDrawerState | null>(null);

  // Use ref for pending drawer to avoid stale closure issues
  // The handler is called synchronously in the same render cycle,
  // so useCallback wouldn't have access to the new value yet
  const pendingDrawerRef = useRef<OpenDrawerState | null>(null);

  // Store close handlers for each drawer
  const handlersRef = useRef<Map<DrawerType, () => void>>(new Map());

  const registerHandler = useCallback(
    (drawerType: DrawerType, handler: () => void) => {
      handlersRef.current.set(drawerType, handler);
      return () => {
        handlersRef.current.delete(drawerType);
      };
    },
    [],
  );

  const notifyCloseAccepted = useCallback(() => {
    const pending = pendingDrawerRef.current;

    if (pending) {
      // Switching drawers
      setOpenDrawer(pending);
      pendingDrawerRef.current = null;
    } else {
      // Closing to empty
      setOpenDrawer(null);
    }
  }, []);

  const notifyCloseRejected = useCallback(() => {
    pendingDrawerRef.current = null;
  }, []);

  const requestOpen = useCallback(
    (state: OpenDrawerState) => {
      const currentType = openDrawer?.type;

      if (currentType && currentType !== state.type) {
        // Another drawer is open, store pending and call its close handler
        pendingDrawerRef.current = state;
        const handler = handlersRef.current.get(currentType);
        if (handler) {
          handler();
        } else {
          // No handler registered for the open drawer - auto-accept close
          notifyCloseAccepted();
        }
      } else if (!currentType) {
        // No drawer open, open immediately
        setOpenDrawer(state);
      } else {
        // If same drawer requested just refresh the
        // state as the data may have changed
        setOpenDrawer(state);
      }
    },
    [openDrawer?.type, notifyCloseAccepted],
  );

  const requestClose = useCallback(() => {
    const currentType = openDrawer?.type;
    if (!currentType) {
      return;
    }
    const handler = handlersRef.current.get(currentType);
    if (handler) {
      handler();
    } else {
      // No handler registered - auto-accept close
      notifyCloseAccepted();
    }
  }, [openDrawer?.type, notifyCloseAccepted]);

  return (
    <SideDrawerCoordinatorContext
      value={{
        openDrawer,
        requestOpen,
        requestClose,
        notifyCloseAccepted,
        notifyCloseRejected,
        registerHandler,
      }}
    >
      {children}
    </SideDrawerCoordinatorContext>
  );
};

export const useSideDrawerCoordinator =
  (): SideDrawerCoordinatorContextProps => {
    const context = useContext(SideDrawerCoordinatorContext);
    if (!context) {
      throw new Error(
        "useSideDrawerCoordinator must be used within a SideDrawerCoordinatorProvider",
      );
    }
    return context;
  };

// ============================================================================
// useSideDrawer hook (for drawer components to interact with the coordinator)
// ============================================================================

type UseSideDrawerOptions = {
  /**
   * Called when coordinator requests this drawer to close.
   * Receives accept/reject callbacks as arguments to avoid closure issues.
   */
  onCloseRequested?: (accept: () => void, reject: () => void) => void;
};

// Type maneuvers so the open function is type safe

type DrawerStateByType<T extends DrawerType> = Extract<
  OpenDrawerState,
  { type: T }
>;

type DrawerOpenArgs<T extends DrawerType> =
  DrawerStateByType<T> extends { data: infer D } ? [data: D] : [];

type UseSideDrawerResult<T extends DrawerType> = {
  /** Whether this drawer is currently open */
  isOpen: boolean;
  /** Open this drawer */
  open: (...args: DrawerOpenArgs<T>) => void;
  /** Notify coordinator that close was accepted */
  notifyCloseAccepted: () => void;
  /** Notify coordinator that close was rejected (e.g., user clicked "Keep editing") */
  notifyCloseRejected: () => void;
};

/**
 * Hook for drawer components to interact with the coordinator.
 */
export const useSideDrawer = <T extends DrawerType>(
  drawerId: T,
  options?: UseSideDrawerOptions,
): UseSideDrawerResult<T> => {
  const {
    openDrawer,
    notifyCloseAccepted,
    notifyCloseRejected,
    registerHandler,
    requestOpen,
  } = useSideDrawerCoordinator();

  const isOpen = openDrawer?.type === drawerId;

  // Register the close handler - only if onCloseRequested is provided
  // This prevents components that only use open() from overwriting handlers
  // that have actual close logic
  const onCloseRequestedRef = useRef(options?.onCloseRequested);
  onCloseRequestedRef.current = options?.onCloseRequested;

  useEffect(() => {
    // Only register if we have an onCloseRequested callback
    // Components that just want open() shouldn't register a handler
    if (!options?.onCloseRequested) {
      return;
    }

    const handler = () => {
      if (onCloseRequestedRef.current) {
        onCloseRequestedRef.current(notifyCloseAccepted, notifyCloseRejected);
      } else {
        // Shouldn't reach here since we only register when onCloseRequested exists
        notifyCloseAccepted();
      }
    };
    return registerHandler(drawerId, handler);
  }, [
    drawerId,
    registerHandler,
    notifyCloseAccepted,
    notifyCloseRejected,
    options?.onCloseRequested,
  ]);

  const open = useCallback(
    (...args: DrawerOpenArgs<T>) => {
      const state =
        args.length > 0
          ? ({ type: drawerId, data: args[0] } as DrawerStateByType<T>)
          : ({ type: drawerId } as DrawerStateByType<T>);

      requestOpen(state);
    },
    [drawerId, requestOpen],
  );

  return {
    isOpen,
    open,
    notifyCloseAccepted,
    notifyCloseRejected,
  };
};

// ============================================================================
// SideDrawer Component
// ============================================================================

type SideDrawerProps = {
  children: React.ReactNode;
  /** Type identifier for this drawer (used by coordinator) */
  drawerId: DrawerType;
  width?: "default" | "wide";
  dataLoc?: string;
  /** Header configuration - if provided, renders a standard header */
  header?: SideDrawerHeaderConfig;
};

type SideDrawerHeaderConfig = {
  title: React.ReactNode;
  /** Content rendered immediately after the title (e.g., status indicator) */
  titleSuffix?: React.ReactNode;
  description?: string;
  /** Extra content rendered to the left of the close button */
  extraActions?: React.ReactNode;
  closeDataLoc?: string;
  reverseTitleAndDescription?: boolean;
  icon?: React.ReactNode;
};

const WIDTH_CLASSES = {
  default: "w-[720px] min-w-[464px] max-w-[40vw]",
  wide: "w-[800px] min-w-[600px] max-w-[40vw]",
} as const;

// shadow-lg dimensions with reduced opacity
const SUBTLE_SHADOW =
  "0 10px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -4px rgba(0,0,0,0.07)";

export const SideDrawer: React.FC<SideDrawerProps> = ({
  children,
  drawerId,
  width = "default",
  dataLoc,
  header,
}) => {
  const { openDrawer, requestClose } = useSideDrawerCoordinator();

  const isOpen = openDrawer?.type === drawerId;

  // Handle Escape key to dismiss drawer
  useKeyDown(["Escape"], (e) => {
    if (!isOpen) return;
    e.preventDefault();
    requestClose();
  });

  return (
    <div
      className={twMerge(
        WIDTH_CLASSES[width],
        "h-full flex-shrink-0 border-gray-200 border-l bg-white",
        header && "flex flex-col",
        !isOpen && "hidden",
      )}
      data-loc={dataLoc}
      style={{ boxShadow: isOpen ? SUBTLE_SHADOW : undefined }}
    >
      {header && (
        <div className="flex-shrink-0 px-5 pt-4">
          <div className="flex items-start justify-between border-gray-200 border-b pb-4">
            <div className="flex items-center gap-2">
              {header.icon}
              <div
                className={twJoin(
                  header.reverseTitleAndDescription && "flex flex-col-reverse",
                )}
              >
                <h3 className="flex items-center gap-1.5 font-inter-semibold-13px text-gray-800">
                  {header.title}
                  {header.titleSuffix}
                </h3>
                {header.description && (
                  <p className="mt-1 font-inter-normal-12px text-gray-500">
                    {header.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {header.extraActions}
              <IconButton
                dataLoc={header.closeDataLoc}
                icon={faTimes}
                size="xs"
                onClick={requestClose}
              />
            </div>
          </div>
        </div>
      )}
      {header ? <div className="min-h-0 flex-1">{children}</div> : children}
    </div>
  );
};

// ============================================================================
// DrawerContainer Component
// ============================================================================

type DrawerContainerProps = {
  children: React.ReactNode;
};

/**
 * Wrapper component for drawer content that provides positioning context.
 */
export const DrawerContainer: React.FC<DrawerContainerProps> = ({
  children,
}) => {
  return <div className="relative">{children}</div>;
};
